import { useState, useEffect } from 'react';
import { ProductCategory, PriceFormula } from '@/types';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Calculator, Percent, RefreshCw } from 'lucide-react';

interface CategoryFormData {
  name: string;
  description: string;
  price_formula: PriceFormula;
  price_multiplier: number;
  margin_percentage: number;
  price_round_to: number;
  list_price_multiplier: number;
}

const FORMULA_OPTIONS: { value: PriceFormula; label: string; description: string }[] = [
  { value: 'multiply_round_up', label: 'Multiplicador (redondeado)', description: 'Costo × multiplicador, redondeado al múltiplo superior' },
  { value: 'multiply_exact', label: 'Multiplicador (exacto)', description: 'Costo × multiplicador, sin redondeo' },
  { value: 'margin_percentage', label: 'Porcentaje de margen', description: 'Costo + porcentaje%, redondeado al múltiplo más cercano' },
  { value: 'fixed_margin', label: 'Margen fijo', description: 'Costo + monto fijo' },
];

const DEFAULT_FORM_DATA: CategoryFormData = {
  name: '',
  description: '',
  price_formula: 'multiply_round_up',
  price_multiplier: 2.0,
  margin_percentage: 30.0,
  price_round_to: 100,
  list_price_multiplier: 1.10,
};

interface CategoryFormModalProps {
  category?: ProductCategory | null;
  onSave: (data: Partial<CategoryFormData>) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  onRecalculate?: (categoryId: number) => Promise<void>;
  isRecalculating?: boolean;
}

export default function CategoryFormModal({
  category,
  onSave,
  onClose,
  isLoading,
  onRecalculate,
  isRecalculating,
}: CategoryFormModalProps) {
  const [formData, setFormData] = useState<CategoryFormData>(DEFAULT_FORM_DATA);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        description: category.description || '',
        price_formula: category.price_formula || 'multiply_round_up',
        price_multiplier: category.price_multiplier || 2.0,
        margin_percentage: category.margin_percentage || 30.0,
        price_round_to: category.price_round_to || 100,
        list_price_multiplier: category.list_price_multiplier || 1.10,
      });
    } else {
      setFormData(DEFAULT_FORM_DATA);
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const categoryData = {
      name: formData.name,
      description: formData.description || undefined,
      price_formula: formData.price_formula,
      price_multiplier: formData.price_multiplier,
      margin_percentage: formData.margin_percentage,
      price_round_to: formData.price_round_to,
      list_price_multiplier: formData.list_price_multiplier,
    };
    await onSave(categoryData);

    // Auto-recalculate if list_price_multiplier changed
    const originalMultiplier = category?.list_price_multiplier || 1.10;
    if (category && onRecalculate && formData.list_price_multiplier !== originalMultiplier) {
      await onRecalculate(category.id);
    }

    onClose();
  };

  // Calcula precio de ejemplo basado en la fórmula seleccionada
  const calculateExamplePrice = (cost: number): number => {
    const { price_formula, price_multiplier, margin_percentage, price_round_to } = formData;

    if (price_formula === 'multiply_round_up') {
      const multiplied = cost * price_multiplier;
      return Math.ceil(multiplied / price_round_to) * price_round_to;
    } else if (price_formula === 'multiply_exact') {
      return Math.round(cost * price_multiplier * 100) / 100;
    } else if (price_formula === 'margin_percentage') {
      const margin = cost * margin_percentage / 100;
      const priceWithMargin = cost + margin;
      return Math.ceil(priceWithMargin / price_round_to) * price_round_to;
    } else if (price_formula === 'fixed_margin') {
      return Math.round((cost + price_multiplier) * 100) / 100;
    }
    return cost * 2;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Nombre *"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Ej: Vacunas, Medicamentos..."
        required
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Descripción
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
          placeholder="Descripción opcional..."
        />
      </div>

      {/* Configuración de fórmula de precios */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="h-4 w-4 text-taupe-600" />
          <h3 className="font-medium text-gray-900">Fórmula de Precios</h3>
        </div>

        <div className="space-y-3">
          {/* Selector de fórmula */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Tipo de cálculo
            </label>
            <select
              value={formData.price_formula}
              onChange={(e) => setFormData({ ...formData, price_formula: e.target.value as PriceFormula })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
            >
              {FORMULA_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {FORMULA_OPTIONS.find(o => o.value === formData.price_formula)?.description}
            </p>
          </div>

          {/* Campos según la fórmula seleccionada */}
          <div className="grid grid-cols-2 gap-3">
            {/* Multiplicador - mostrar para multiply_* */}
            {(formData.price_formula === 'multiply_round_up' || formData.price_formula === 'multiply_exact') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Multiplicador
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  value={formData.price_multiplier}
                  onChange={(e) => setFormData({ ...formData, price_multiplier: parseFloat(e.target.value) || 2 })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">Ej: 2.0 = costo × 2</p>
              </div>
            )}

            {/* Porcentaje de margen - solo para margin_percentage */}
            {formData.price_formula === 'margin_percentage' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Porcentaje de margen
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.margin_percentage}
                    onChange={(e) => setFormData({ ...formData, margin_percentage: parseFloat(e.target.value) || 30 })}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Ej: 30 = costo + 30%</p>
              </div>
            )}

            {/* Margen fijo - solo para fixed_margin */}
            {formData.price_formula === 'fixed_margin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Margen fijo ($)
                </label>
                <input
                  type="number"
                  step="100"
                  min="0"
                  value={formData.price_multiplier}
                  onChange={(e) => setFormData({ ...formData, price_multiplier: parseFloat(e.target.value) || 500 })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">Monto fijo a sumar al costo</p>
              </div>
            )}

            {/* Redondear al múltiplo - para multiply_round_up y margin_percentage */}
            {(formData.price_formula === 'multiply_round_up' || formData.price_formula === 'margin_percentage') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Redondear a múltiplo de
                </label>
                <select
                  value={formData.price_round_to}
                  onChange={(e) => setFormData({ ...formData, price_round_to: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                >
                  <option value={10}>$10</option>
                  <option value={50}>$50</option>
                  <option value={100}>$100</option>
                  <option value={500}>$500</option>
                  <option value={1000}>$1.000</option>
                </select>
              </div>
            )}
          </div>

          {/* Precio tarjeta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Recargo precio tarjeta
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                min="1"
                value={formData.list_price_multiplier}
                onChange={(e) => setFormData({ ...formData, list_price_multiplier: parseFloat(e.target.value) || 1.10 })}
                className="w-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
              />
              <span className="text-sm text-gray-600">
                = +{Math.round((formData.list_price_multiplier - 1) * 100)}% sobre precio efectivo
              </span>
            </div>
            {category && onRecalculate && (
              <button
                type="button"
                onClick={async () => {
                  // Save category first so backend uses the new multiplier
                  await onSave({
                    name: formData.name,
                    description: formData.description || undefined,
                    price_formula: formData.price_formula,
                    price_multiplier: formData.price_multiplier,
                    margin_percentage: formData.margin_percentage,
                    price_round_to: formData.price_round_to,
                    list_price_multiplier: formData.list_price_multiplier,
                  });
                  await onRecalculate(category.id);
                  onClose();
                }}
                disabled={isRecalculating || isLoading}
                className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isRecalculating ? 'animate-spin' : ''}`} />
                {isRecalculating ? 'Recalculando...' : 'Guardar y aplicar a todos los productos'}
              </button>
            )}
          </div>

          {/* Ejemplo de cálculo */}
          <div className="bg-taupe-50 rounded-lg p-3 mt-3">
            <p className="text-sm font-medium text-taupe-800 mb-2">Ejemplo de cálculo:</p>
            <div className="text-sm text-taupe-600 space-y-1">
              <p>Costo: $1.000</p>
              <p>Precio efectivo: <span className="font-semibold text-taupe-800">${calculateExamplePrice(1000).toLocaleString('es-AR')}</span></p>
              <p>Precio tarjeta: <span className="font-semibold text-taupe-800">${Math.round(calculateExamplePrice(1000) * formData.list_price_multiplier).toLocaleString('es-AR')}</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" loading={isLoading}>
          {category ? 'Guardar' : 'Crear'}
        </Button>
      </div>
    </form>
  );
}
